export const CHAT_FLOW_VIEW_BOX = '0 0 250 124.56';

/** Intro blend from rest pose (scale 1) into the wave loop. */
export const CHAT_FLOW_INTRO_DURATION_MS = 600;

const CENTER_Y = 72.29;
const MIN_HEIGHT = 0.1;
const MAX_HEIGHT = 1;

/** Opacity steps aligned with `chatFlowMono.svg`. */
export const CHAT_FLOW_WAVE_OPACITIES = [1, 0.78, 0.6, 0.42];

export const CHAT_FLOW_WAVE_DEFS = [
  {
    start: [4.12, 72.29],
    segments: [
      [
        [0.69, 0.28],
        [26.68, 10.63],
        [43.67, 10.63],
      ],
      [
        [10.35, 0],
        [22.19, 6.38],
        [35.89, 13.77],
      ],
      [
        [20.21, 10.9],
        [44.25, 23.86],
        [75.22, 23.86],
      ],
      [
        [52.3, 0],
        [86.08, -44.66],
        [86.98, -45.87],
      ],
    ],
    speed: 1.62,
    phase: 0,
  },
  {
    start: [4.13, 72.29],
    segments: [
      [
        [5.54, -3.86],
        [38.34, -25.17],
        [61.46, -25.17],
      ],
      [
        [18.59, 0],
        [38.1, 12.74],
        [57.79, 25.61],
      ],
      [
        [20.8, 13.59],
        [42.3, 27.63],
        [63.96, 27.63],
      ],
      [
        [29.95, 0],
        [57.8, -24.99],
        [58.55, -25.67],
      ],
    ],
    speed: 1.48,
    phase: 0,
  },
  {
    start: [4.12, 72.29],
    segments: [
      [
        [0.64, 0.28],
        [24.75, 10.71],
        [46.72, 10.71],
      ],
      [
        [23.25, 0],
        [42.98, -10.52],
        [62.91, -21.14],
      ],
      [
        [18.81, -10.03],
        [37.44, -19.95],
        [58.14, -19.95],
      ],
      [
        [40.24, 0],
        [73.11, 31.92],
        [73.99, 32.79],
      ],
    ],
    speed: 1.57,
    phase: 0,
  },
  {
    start: [4, 72.29],
    lineTo: [22.59, 72.29],
    segments: [
      [
        [48.23, 0],
        [68.92, -21.59],
        [87.07, -40.53],
      ],
      [
        [14.23, -14.84],
        [26.6, -27.76],
        [49.25, -27.76],
      ],
      [
        [37.19, 0],
        [79.95, 59.81],
        [87.09, 70.69],
      ],
    ],
    speed: 1.39,
    phase: 0,
  },
];

/** Deterministic per-wave variation (stable across mounts). */
export const CHAT_FLOW_WAVE_RUNTIME = CHAT_FLOW_WAVE_DEFS.map((def, index) => ({
  def,
  extraPhase: index * 1.73 + 0.41,
  extraSpeed: 0.88 + (index % 4) * 0.09,
}));

function oscillator(t, speed, phase) {
  return (
    Math.sin(t * speed + phase) * 0.58 +
    Math.sin(t * speed * 0.37 + phase * 1.91) * 0.3 +
    Math.sin(t * speed * 0.13 + phase * 2.73) * 0.12
  );
}

function normalize(value) {
  return (value + 1) / 2;
}

function scaleY(y, scale, keepFixed = false) {
  if (keepFixed) return y;
  return CENTER_Y + (y - CENTER_Y) * scale;
}

export function buildChatFlowWavePath(def, scale) {
  let current = [...def.start];
  let d = `M ${current[0]} ${current[1]}`;

  if (def.lineTo) {
    current = [...def.lineTo];
    d += ` L ${current[0]} ${current[1]}`;
  }

  const finalSegment = def.segments[def.segments.length - 1];

  def.segments.forEach(segment => {
    const [c1, c2, end] = segment;

    const p1 = [current[0] + c1[0], current[1] + c1[1]];
    const p2 = [current[0] + c2[0], current[1] + c2[1]];
    const p3 = [current[0] + end[0], current[1] + end[1]];

    const isLastSegment = segment === finalSegment;

    d += ` C ${p1[0]} ${scaleY(p1[1], scale)} ${p2[0]} ${scaleY(p2[1], scale)} ${p3[0]} ${scaleY(
      p3[1],
      scale,
      isLastSegment,
    )}`;

    current = p3;
  });

  return d;
}

function easeInOut(t) {
  return t * t * (3 - 2 * t);
}

function chatFlowAnimatedScaleAtTime(tSeconds, wave) {
  const movement = oscillator(
    tSeconds * wave.extraSpeed,
    wave.def.speed,
    wave.def.phase + wave.extraPhase,
  );
  const amount = normalize(movement);
  return MIN_HEIGHT + amount * (MAX_HEIGHT - MIN_HEIGHT);
}

/** Scale at `ms` with intro easing from the static pose (scale 1). */
export function chatFlowWaveScaleAtMs(ms, startTimeMs, wave) {
  const animatedScale = chatFlowAnimatedScaleAtTime(ms / 1000, wave);

  const introProgress = Math.min(1, (ms - startTimeMs) / CHAT_FLOW_INTRO_DURATION_MS);
  const animationStrength = easeInOut(introProgress);

  return 1 + (animatedScale - 1) * animationStrength;
}
